import { M365SignIn } from '../../types/m365Users'

interface SignInDetailPanelProps {
  signIn: M365SignIn
  onClose: () => void
}

export function SignInDetailPanel({ signIn, onClose }: SignInDetailPanelProps) {
  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString(undefined, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  }

  const getLocationString = () => {
    if (!signIn.location) return null
    const parts = [signIn.location.city, signIn.location.state, signIn.location.countryOrRegion].filter(Boolean)
    return parts.length > 0 ? parts.join(', ') : null
  }

  const isSuccess = signIn.status.errorCode === 0

  const getConditionalAccessBadge = () => {
    if (!signIn.conditionalAccessStatus) return null

    const statusConfig: Record<string, { color: string; label: string }> = {
      success: { color: 'bg-green-100 text-green-800', label: 'Success' },
      failure: { color: 'bg-red-100 text-red-800', label: 'Blocked' },
      notApplied: { color: 'bg-gray-100 text-gray-800', label: 'Not Applied' },
    }

    const config = statusConfig[signIn.conditionalAccessStatus] || {
      color: 'bg-gray-100 text-gray-800',
      label: signIn.conditionalAccessStatus,
    }

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    )
  }

  const getRiskBadge = (riskLevel?: string) => {
    if (!riskLevel || riskLevel === 'none') return null

    const riskConfig: Record<string, { color: string; label: string }> = {
      low: { color: 'bg-yellow-100 text-yellow-800', label: 'Low' },
      medium: { color: 'bg-orange-100 text-orange-800', label: 'Medium' },
      high: { color: 'bg-red-100 text-red-800', label: 'High' },
      hidden: { color: 'bg-gray-100 text-gray-800', label: 'Hidden' },
      unknownFutureValue: { color: 'bg-gray-100 text-gray-800', label: 'Unknown' },
    }

    const config = riskConfig[riskLevel] || { color: 'bg-gray-100 text-gray-800', label: riskLevel }

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    )
  }

  const hasRiskData = signIn.riskLevelDuringSignIn && signIn.riskLevelDuringSignIn !== 'none'

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="fixed inset-y-0 right-0 max-w-md w-full bg-white shadow-xl flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-white">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Sign-In Details</h2>
            <p className="text-sm text-gray-500">{formatDateTime(signIn.createdDateTime)}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 p-2 rounded-md hover:bg-gray-100"
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
          {/* Status Section */}
          <div className={`rounded-lg p-4 ${isSuccess ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
            <div className="flex items-center">
              {isSuccess ? (
                <div className="flex-shrink-0 h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                  <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              ) : (
                <div className="flex-shrink-0 h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
                  <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
              )}
              <div className="ml-4">
                <h3 className={`text-lg font-medium ${isSuccess ? 'text-green-800' : 'text-red-800'}`}>
                  {isSuccess ? 'Sign-In Successful' : 'Sign-In Failed'}
                </h3>
                {!isSuccess && (
                  <p className="text-sm text-red-700">Error Code: {signIn.status.errorCode}</p>
                )}
              </div>
            </div>

            {!isSuccess && signIn.status.failureReason && (
              <div className="mt-3 pt-3 border-t border-red-200">
                <p className="text-sm font-medium text-red-800">Failure Reason</p>
                <p className="text-sm text-red-700 mt-1">{signIn.status.failureReason}</p>
              </div>
            )}

            {signIn.status.additionalDetails && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <p className="text-sm font-medium text-gray-700">Additional Details</p>
                <p className="text-sm text-gray-600 mt-1">{signIn.status.additionalDetails}</p>
              </div>
            )}
          </div>

          {/* Application Section */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Application</h3>
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Application</span>
                <span className="text-sm font-medium text-gray-900">{signIn.appDisplayName || '-'}</span>
              </div>
              {signIn.resourceDisplayName && (
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Resource</span>
                  <span className="text-sm font-medium text-gray-900">{signIn.resourceDisplayName}</span>
                </div>
              )}
              {signIn.clientAppUsed && (
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Client App</span>
                  <span className="text-sm font-medium text-gray-900">{signIn.clientAppUsed}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Sign-In Type</span>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  signIn.isInteractive ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  {signIn.isInteractive ? 'Interactive' : 'Non-Interactive'}
                </span>
              </div>
              {signIn.appId && (
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">App ID</span>
                  <span className="text-xs font-mono text-gray-600 truncate max-w-[200px]" title={signIn.appId}>
                    {signIn.appId}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Conditional Access Section */}
          {signIn.conditionalAccessStatus && (
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Conditional Access</h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Status</span>
                  {getConditionalAccessBadge()}
                </div>
              </div>
            </div>
          )}

          {/* Risk Assessment Section */}
          {hasRiskData && (
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Risk Assessment</h3>
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Risk Level</span>
                  {getRiskBadge(signIn.riskLevelDuringSignIn)}
                </div>
                {signIn.riskDetail && signIn.riskDetail !== 'none' && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Risk Detail</span>
                    <span className="text-sm font-medium text-gray-900">{signIn.riskDetail}</span>
                  </div>
                )}
                {signIn.riskState && signIn.riskState !== 'none' && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Risk State</span>
                    <span className="text-sm font-medium text-gray-900">{signIn.riskState}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Location & Network Section */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Location & Network</h3>
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">IP Address</span>
                <span className="text-sm font-mono text-gray-900">{signIn.ipAddress || '-'}</span>
              </div>
              {getLocationString() && (
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Location</span>
                  <span className="text-sm font-medium text-gray-900">{getLocationString()}</span>
                </div>
              )}
            </div>
          </div>

          {/* Device Section */}
          {signIn.deviceDetail && (
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Device</h3>
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                {signIn.deviceDetail.browser && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Browser</span>
                    <span className="text-sm font-medium text-gray-900">{signIn.deviceDetail.browser}</span>
                  </div>
                )}
                {signIn.deviceDetail.operatingSystem && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Operating System</span>
                    <span className="text-sm font-medium text-gray-900">{signIn.deviceDetail.operatingSystem}</span>
                  </div>
                )}
                {signIn.deviceDetail.displayName && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Device Name</span>
                    <span className="text-sm font-medium text-gray-900">{signIn.deviceDetail.displayName}</span>
                  </div>
                )}
                {signIn.deviceDetail.isCompliant !== undefined && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">Compliant</span>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      signIn.deviceDetail.isCompliant ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {signIn.deviceDetail.isCompliant ? 'Yes' : 'No'}
                    </span>
                  </div>
                )}
                {signIn.deviceDetail.isManaged !== undefined && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">Managed</span>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      signIn.deviceDetail.isManaged ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {signIn.deviceDetail.isManaged ? 'Yes' : 'No'}
                    </span>
                  </div>
                )}
                {signIn.deviceDetail.trustType && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Trust Type</span>
                    <span className="text-sm font-medium text-gray-900">{signIn.deviceDetail.trustType}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* User Info Section */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">User</h3>
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">User Principal Name</span>
                <span className="text-sm font-medium text-gray-900 truncate max-w-[200px]" title={signIn.userPrincipalName}>
                  {signIn.userPrincipalName}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            className="w-full px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}
